import { useCentralizedLinkClick } from '@/hooks';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import CrossIcon from '@/storybook/icons/CrossIcon';
import FilterIcon from '@/storybook/icons/FilterIcon';

import cx from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import CollapsiblePanel from '../CollapsiblePanel/CollapsiblePanel';
import styles from './ZippedPanel.module.css';

const zippedPanelTestIds = {
  container: 'ces-zipped-panel-container',
  contentPanel: 'ces-zipped-panel-content',
  zipperWrapper: 'ces-zipped-panel-zipper-wrapper',
  zipper: 'ces-zipped-panel-zipper',
} as const;

interface ZipperConfig {
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface ZippedPanelProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  zipperOpen?: ZipperConfig;
  zipperClosed?: ZipperConfig;
  defaultState?: boolean;
  children: React.ReactNode;
  className?: string;
}

const ZippedPanel: React.FC<ZippedPanelProps> = ({
  zipperOpen,
  zipperClosed,
  defaultState = false,
  children,
  className,
  ...props
}) => {
  const defaultOpen = useMemo(
    () => ({
      label: 'Close Panel',
      icon: <CrossIcon />,
    }),
    [],
  );

  const defaultClosed = useMemo(
    () => ({
      label: 'Open Panel',
      icon: <FilterIcon />,
    }),
    [],
  );

  const zipperOpenConfig = zipperOpen ?? defaultOpen;
  const zipperClosedConfig = zipperClosed ?? defaultClosed;

  const [open, setOpen] = useState(defaultState);
  const { trackButtonClick } = useCentralizedLinkClick();

  const toggleOpen = useCallback(() => {
    setOpen(previousState => {
      const nextState = !previousState;
      trackButtonClick({
        innertext: nextState
          ? typeof zipperOpenConfig.label === 'string'
            ? zipperOpenConfig.label
            : 'Show Filters'
          : typeof zipperClosedConfig.label === 'string'
            ? zipperClosedConfig.label
            : 'Hide Filters',
        position: AdobeAnalyticsConst.POSITIONS.NAVIGATION_BAR,
        name: AdobeAnalyticsConst.LINKCLICK_NAMES.SHOW_FILTERS,
      });
      return nextState;
    });
  }, [zipperOpenConfig.label, zipperClosedConfig.label, trackButtonClick]);

  const zipperOptions = useMemo(() => {
    return open ? { ...defaultOpen, ...zipperOpen } : { ...defaultClosed, ...zipperClosed };
  }, [open, defaultOpen, defaultClosed, zipperOpen, zipperClosed]);

  return (
    <div className={cx(styles.zipperPanel, className)} data-testid={zippedPanelTestIds.container}>
      <div className={styles.contentPanel} data-testid={zippedPanelTestIds.contentPanel}>
        <CollapsiblePanel open={open} openedPanelHeight={'100vh'}>
          <div className={styles.contentWrapper}>{children}</div>
        </CollapsiblePanel>
      </div>
      <div className={cx(styles.zipperWrapper, { [styles.open]: open })} data-testid={zippedPanelTestIds.zipperWrapper}>
        <button
          role="button"
          aria-expanded={open}
          className={styles.zipper}
          data-testid={zippedPanelTestIds.zipper}
          onClick={toggleOpen}
          {...props}
        >
          <span className={styles.iconWrapper}>{zipperOptions.icon}</span>
          {zipperOptions.label}
        </button>
      </div>
    </div>
  );
};

ZippedPanel.displayName = 'ZippedPanel';

export default ZippedPanel;
